import React, { useEffect, useState, useCallback } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import {
  Loader2, Wallet, Users, DollarSign, TrendingUp, Briefcase,
  ArrowDownToLine, Banknote, BarChart3, Percent, CheckCircle, Clock,
  ArrowUpRight, Zap, Target
} from "lucide-react";
import { Card } from "../shared/UIComponents";
import SyndicationDashboard from "./SyndicationDashboard";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CapitalEngineDashboard({ onEntityNavigate }) {
  const [overview, setOverview] = useState(null);
  const [revenue, setRevenue] = useState(null);
  const [dealAlloc, setDealAlloc] = useState(null);
  const [loading, setLoading] = useState(true);

  const token = localStorage.getItem("ic_token");
  const headers = { Authorization: `Bearer ${token}` };

  const fetchData = useCallback(async () => {
    try {
      const [ovRes, revRes, allocRes] = await Promise.all([
        axios.get(`${API}/capital/overview`, { headers }),
        axios.get(`${API}/capital/revenue`, { headers }),
        axios.get(`${API}/admin/capital/deal-allocations`, { headers }),
      ]);
      setOverview(ovRes.data);
      setRevenue(revRes.data);
      setDealAlloc(allocRes.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="h-8 w-8 animate-spin text-emerald-700" /></div>;
  if (!overview) return <div className="text-center py-12 text-sm text-[#888]">No capital data yet.</div>;

  const cap = overview.capital || {};
  const opps = overview.opportunities || {};
  const commits = overview.commitments || {};
  const dist = overview.distributions || {};
  const rev = revenue?.summary || {};
  const feeConfig = revenue?.fee_config || {};
  const fees = revenue?.fees || [];

  return (
    <div className="space-y-6" data-testid="capital-engine-dashboard">
      {/* AUM Hero */}
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
        className="rounded-2xl border border-emerald-200 bg-gradient-to-br from-emerald-900 to-emerald-950 p-6 shadow-lg">
        <div className="flex items-center gap-3 mb-5">
          <div className="p-2.5 rounded-xl bg-white/10 border border-white/20">
            <Wallet className="h-5 w-5 text-emerald-300" />
          </div>
          <div>
            <h3 className="text-sm font-bold text-white/70 uppercase tracking-wider">Capital Engine Overview</h3>
            <p className="text-xs text-white/40">Total Assets Under Management</p>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { label: "Total AUM", value: cap.total_aum, color: "text-emerald-300", highlight: true },
            { label: "Available Capital", value: cap.total_available, color: "text-blue-300" },
            { label: "Deployed Capital", value: cap.total_deployed, color: "text-gold" },
            { label: "Active Investors", value: null, count: cap.investor_count, color: "text-white" },
          ].map((s, i) => (
            <motion.div key={s.label} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.06 }}
              className={`rounded-xl ${s.highlight ? "bg-white/15 border border-white/20" : "bg-white/8 border border-white/10"} p-4`}
              data-testid={`ce-${s.label.toLowerCase().replace(/ /g, '-')}`}>
              <div className="text-[9px] text-white/50 uppercase tracking-wider font-semibold mb-1">{s.label}</div>
              {s.count != null
                ? <div className={`text-3xl font-black ${s.color}`}>{s.count}</div>
                : <div className={`text-2xl font-black ${s.color}`}>${((s.value || 0) / 1e6).toFixed(2)}M</div>
              }
            </motion.div>
          ))}
        </div>
      </motion.div>

      {/* Deal Funnel + Revenue */}
      <div className="grid lg:grid-cols-2 gap-5">
        {/* Deal Funnel */}
        <Card hover={false}>
          <h4 className="text-sm font-bold text-[#0A0A0A] uppercase tracking-wider mb-4 flex items-center gap-2">
            <Briefcase className="h-4 w-4 text-gold-dark" /> Capital Pipeline
          </h4>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
            <div className="rounded-xl bg-cream border border-cream-warm p-3 text-center">
              <div className="text-2xl font-bold text-blue-600">{opps.open}</div>
              <div className="text-[9px] text-[#999] uppercase tracking-wider">Open Deals</div>
            </div>
            <div className="rounded-xl bg-cream border border-cream-warm p-3 text-center">
              <div className="text-2xl font-bold text-emerald-600">{opps.funded}</div>
              <div className="text-[9px] text-[#999] uppercase tracking-wider">Funded</div>
            </div>
            <div className="rounded-xl bg-cream border border-cream-warm p-3 text-center">
              <div className="text-2xl font-bold text-[#444]">{opps.total}</div>
              <div className="text-[9px] text-[#999] uppercase tracking-wider">Total</div>
            </div>
          </div>
          <div className="space-y-2">
            <div className="flex items-center justify-between rounded-xl bg-cream border border-cream-warm p-3">
              <div className="flex items-center gap-2 text-sm text-[#666]">
                <CheckCircle className="h-4 w-4 text-emerald-500" /> Total Commitments
              </div>
              <span className="font-bold text-[#0A0A0A]">{commits.total}</span>
            </div>
            <div className="flex items-center justify-between rounded-xl bg-cream border border-cream-warm p-3">
              <div className="flex items-center gap-2 text-sm text-[#666]">
                <ArrowDownToLine className="h-4 w-4 text-purple-500" /> Distributions
              </div>
              <span className="font-bold text-[#0A0A0A]">{dist.count} (${((dist.total_distributed || 0) / 1e3).toFixed(1)}K)</span>
            </div>
          </div>
        </Card>

        {/* Revenue Summary */}
        <Card hover={false}>
          <h4 className="text-sm font-bold text-[#0A0A0A] uppercase tracking-wider mb-4 flex items-center gap-2">
            <DollarSign className="h-4 w-4 text-gold-dark" /> Platform Revenue
          </h4>
          <div className="rounded-xl bg-emerald-50 border border-emerald-200 p-4 mb-4">
            <div className="text-[9px] text-emerald-600 uppercase tracking-wider font-semibold mb-1">Total Revenue</div>
            <div className="text-3xl font-black text-emerald-700">${((rev.total_revenue || 0) / 1e3).toFixed(1)}K</div>
            <div className="text-xs text-emerald-600/70 mt-1">
              From ${((rev.total_funded_volume || 0) / 1e6).toFixed(2)}M funded volume ({rev.deal_count || 0} deals)
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
            <div className="rounded-xl bg-cream border border-cream-warm p-3 text-center">
              <div className="text-lg font-bold text-[#0A0A0A]">${((rev.total_origination || 0) / 1e3).toFixed(1)}K</div>
              <div className="text-[9px] text-[#999] uppercase">Origination</div>
            </div>
            <div className="rounded-xl bg-cream border border-cream-warm p-3 text-center">
              <div className="text-lg font-bold text-[#0A0A0A]">${((rev.total_investor_fees || 0) / 1e3).toFixed(1)}K</div>
              <div className="text-[9px] text-[#999] uppercase">Investor Fee</div>
            </div>
            <div className="rounded-xl bg-cream border border-cream-warm p-3 text-center">
              <div className="text-lg font-bold text-[#0A0A0A]">${((rev.total_servicing || 0) / 1e3).toFixed(1)}K</div>
              <div className="text-[9px] text-[#999] uppercase">Servicing</div>
            </div>
          </div>
          <div className="text-[10px] text-[#888] uppercase tracking-wider font-semibold mb-2">Fee Schedule</div>
          <div className="flex gap-3">
            {[
              { label: "Origination", pct: feeConfig.origination_pct },
              { label: "Platform", pct: feeConfig.investor_platform_pct },
              { label: "Servicing", pct: feeConfig.servicing_pct },
            ].map(f => (
              <div key={f.label} className="flex items-center gap-1.5 rounded-lg bg-cream border border-cream-warm px-2.5 py-1.5">
                <Percent className="h-3 w-3 text-gold-dark" />
                <span className="text-[10px] text-[#666]">{f.label}: <span className="font-bold text-[#333]">{f.pct}%</span></span>
              </div>
            ))}
          </div>
        </Card>
      </div>

      {/* Recent Fee Records */}
      {fees.length > 0 && (
        <Card hover={false}>
          <h4 className="text-sm font-bold text-[#0A0A0A] uppercase tracking-wider mb-4 flex items-center gap-2">
            <Banknote className="h-4 w-4 text-gold-dark" /> Fee History
          </h4>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-cream-warm text-[10px] uppercase tracking-wider text-[#999]">
                  <th className="text-left py-2">Date</th>
                  <th className="text-left py-2">Deal</th>
                  <th className="text-right py-2">Volume</th>
                  <th className="text-right py-2">Origination</th>
                  <th className="text-right py-2">Investor</th>
                  <th className="text-right py-2">Servicing</th>
                  <th className="text-right py-2">Total</th>
                </tr>
              </thead>
              <tbody>
                {fees.map((f, i) => (
                  <tr key={f.fee_id || i} className="border-b border-cream-warm/50 hover:bg-cream/50 transition-colors">
                    <td className="py-2.5 text-[#666]">{new Date(f.created_at).toLocaleDateString()}</td>
                    <td className="py-2.5 font-medium text-[#333]">{f.loan_type_label || "Deal"}</td>
                    <td className="py-2.5 text-right text-[#666]">${(f.funded_amount || 0).toLocaleString()}</td>
                    <td className="py-2.5 text-right text-[#666]">${(f.origination_fee || 0).toLocaleString()}</td>
                    <td className="py-2.5 text-right text-[#666]">${(f.investor_platform_fee || 0).toLocaleString()}</td>
                    <td className="py-2.5 text-right text-[#666]">${(f.servicing_fee || 0).toLocaleString()}</td>
                    <td className="py-2.5 text-right font-bold text-emerald-600">${(f.total_fees || 0).toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* Deal-Level Capital Allocation */}
      {dealAlloc && dealAlloc.deals && dealAlloc.deals.length > 0 && (
        <Card hover={false}>
          <h4 className="text-sm font-bold text-[#0A0A0A] uppercase tracking-wider mb-4 flex items-center gap-2">
            <Target className="h-4 w-4 text-gold-dark" /> Deal Capital Allocation
          </h4>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
            <div className="rounded-xl bg-cream border border-cream-warm p-3 text-center">
              <div className="text-lg font-bold text-[#0A0A0A]">${((dealAlloc.kpis?.total_target || 0) / 1e6).toFixed(2)}M</div>
              <div className="text-[9px] text-[#999] uppercase">Target</div>
            </div>
            <div className="rounded-xl bg-cream border border-cream-warm p-3 text-center">
              <div className="text-lg font-bold text-emerald-600">${((dealAlloc.kpis?.total_raised || 0) / 1e6).toFixed(2)}M</div>
              <div className="text-[9px] text-[#999] uppercase">Raised</div>
            </div>
            <div className="rounded-xl bg-cream border border-cream-warm p-3 text-center">
              <div className="text-lg font-bold text-amber-600">${((dealAlloc.kpis?.total_remaining || 0) / 1e6).toFixed(2)}M</div>
              <div className="text-[9px] text-[#999] uppercase">Remaining</div>
            </div>
            <div className="rounded-xl bg-cream border border-cream-warm p-3 text-center">
              <div className="text-lg font-bold text-blue-600">{dealAlloc.kpis?.avg_funded_pct || 0}%</div>
              <div className="text-[9px] text-[#999] uppercase">Avg Funded</div>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="capital-deal-allocation-table">
              <thead>
                <tr className="border-b border-cream-warm text-[10px] uppercase tracking-wider text-[#999]">
                  <th className="text-left py-2">Deal</th>
                  <th className="text-center py-2">Status</th>
                  <th className="text-right py-2">Target</th>
                  <th className="text-right py-2">Raised</th>
                  <th className="text-right py-2">Remaining</th>
                  <th className="text-center py-2">Investors</th>
                  <th className="text-left py-2 w-32">Progress</th>
                </tr>
              </thead>
              <tbody>
                {dealAlloc.deals.map((d) => (
                  <tr key={d.opportunity_id} className="border-b border-cream-warm/50 hover:bg-cream/50 transition-colors cursor-pointer" onClick={() => onEntityNavigate?.("deal", d.lead_id || d.opportunity_id)} data-testid={`alloc-row-${d.opportunity_id}`}>
                    <td className="py-2.5">
                      <div className="text-sm font-semibold text-[#333] hover:text-emerald-700 transition-colors">{d.borrower_name || d.deal_number || "—"}</div>
                      {d.deal_number && <div className="text-[10px] font-mono text-emerald-600">{d.deal_number}</div>}
                    </td>
                    <td className="py-2.5 text-center">
                      <span className={`px-2 py-0.5 rounded-full text-[9px] font-bold border ${
                        d.status === "funded" ? "bg-green-50 text-green-600 border-green-200" :
                        d.status === "open" ? "bg-blue-50 text-blue-600 border-blue-200" :
                        "bg-slate-50 text-slate-500 border-slate-200"
                      }`}>{d.status}</span>
                    </td>
                    <td className="py-2.5 text-right text-[#666]">${(d.target / 1e3).toFixed(0)}K</td>
                    <td className="py-2.5 text-right font-semibold text-emerald-600">${(d.raised / 1e3).toFixed(0)}K</td>
                    <td className="py-2.5 text-right text-[#888]">
                      {d.remaining > 0 ? `$${(d.remaining / 1e3).toFixed(0)}K` : <span className="text-green-600 font-bold">Closed</span>}
                    </td>
                    <td className="py-2.5 text-center font-semibold text-[#333]">{d.investor_count}</td>
                    <td className="py-2.5">
                      <div className="flex items-center gap-2">
                        <div className="flex-1 h-2 rounded-full bg-[#e8e0d0] overflow-hidden">
                          <div className={`h-full rounded-full transition-all ${d.pct_funded >= 100 ? "bg-green-500" : d.pct_funded >= 50 ? "bg-emerald-500" : "bg-blue-500"}`}
                            style={{ width: `${Math.min(100, d.pct_funded)}%` }} />
                        </div>
                        <span className="text-[10px] font-bold text-[#555] w-10 text-right">{d.pct_funded}%</span>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* Syndication Dashboard */}
      <div className="mt-8">
        <h3 className="text-sm font-bold text-[#333] uppercase tracking-wider mb-4 flex items-center gap-2">
          <Zap className="h-4 w-4 text-gold-dark" /> Capital Syndication
        </h3>
        <SyndicationDashboard />
      </div>
    </div>
  );
}
